import { BLOCK_FIXTURES, type BlockFixtureDefinition } from '@web-studio/core-fixtures';
import { IMPLEMENTED_COMPONENT_TYPES } from '@web-studio/layout-matrix';
import {
  COMPONENT_TYPE_REGISTRY_KEYS,
  GALLERY_LIGHTBOX_VALUES,
  NAV_PRESENTATION_VALUES,
  REGISTRY_BLOCK,
  harmonizedGalleryLightbox,
  isGalleryLightbox,
  isNavPresentation,
  listPresentationsForBlockType,
  resolveBlockTypeForPresentation,
  resolveComponentTypeForRegistryBlock,
  type LayoutComponentType,
  type NavPresentation,
  type RegistryBlockPresentation,
  type RegistryBlockType,
} from '@web-studio/shared-schemas';

export type LabBlockOption = {
  type: RegistryBlockType;
  label: string;
};

export type LabArrangementOption = {
  /** Stable select value — fixture id, or `pres:<RegistryBlockPresentation>`. */
  id: string;
  label: string;
  fixtureId: string;
  componentType: LayoutComponentType;
  presentation: RegistryBlockPresentation;
};

export type LabLookOption = {
  /** Nav chrome look or gallery lightbox look. */
  value: string;
  label: string;
};

const GALLERY_LOOK_LABELS: Record<(typeof GALLERY_LIGHTBOX_VALUES)[number], string> = {
  stage: 'Stage',
  catalog: 'Catalog',
  cinema: 'Cinema',
};

const LOOK_LABELS: Record<NavPresentation, string> = {
  classic: 'Classic',
  overlay: 'Overlay',
  capsule: 'Capsule',
  quiet: 'Quiet',
  frosted: 'Frosted',
  conversion: 'Conversion',
  whisper: 'Whisper',
  editorial: 'Editorial',
};

const BLOCK_LABELS: Partial<Record<RegistryBlockType, string>> = {
  [REGISTRY_BLOCK.type.navigation]: 'Navigation',
  [REGISTRY_BLOCK.type.hero]: 'Hero',
  [REGISTRY_BLOCK.type.staticMenu]: 'Menu',
  [REGISTRY_BLOCK.type.dynamicMenu]: 'Dynamic menu',
  [REGISTRY_BLOCK.type.reservation]: 'Reservation',
  [REGISTRY_BLOCK.type.chefStory]: 'Chef story',
  [REGISTRY_BLOCK.type.imageGallery]: 'Image gallery',
  [REGISTRY_BLOCK.type.tastingTimeline]: 'Tasting timeline',
  [REGISTRY_BLOCK.type.allergyForm]: 'Allergy form',
  [REGISTRY_BLOCK.type.footer]: 'Footer',
  [REGISTRY_BLOCK.type.openingHours]: 'Opening hours',
  [REGISTRY_BLOCK.type.addressMap]: 'Address map',
  [REGISTRY_BLOCK.type.drinksList]: 'Drinks list',
  [REGISTRY_BLOCK.type.staticLocation]: 'Static location',
  [REGISTRY_BLOCK.type.simpleMenuStrip]: 'Simple menu strip',
  [REGISTRY_BLOCK.type.liveBakeryCounter]: 'Bakery counter',
  [REGISTRY_BLOCK.type.socialFeed]: 'Social feed',
  [REGISTRY_BLOCK.type.coffeeSubscription]: 'Coffee subscription',
  [REGISTRY_BLOCK.type.preOrderPickup]: 'Pre-order pickup',
  [REGISTRY_BLOCK.type.cocktailSpotlight]: 'Cocktail spotlight',
  [REGISTRY_BLOCK.type.privatePartyInquiry]: 'Private party',
  [REGISTRY_BLOCK.type.liveTapList]: 'Tap list',
  [REGISTRY_BLOCK.type.vipTableBooking]: 'VIP booking',
  [REGISTRY_BLOCK.type.weeklySchedule]: 'Weekly schedule',
  [REGISTRY_BLOCK.type.cateringInquiry]: 'Catering',
  [REGISTRY_BLOCK.type.faq]: 'FAQ',
  [REGISTRY_BLOCK.type.liveGps]: 'Live GPS',
  [REGISTRY_BLOCK.type.popUpTicket]: 'Pop-up ticket',
  [REGISTRY_BLOCK.type.contact]: 'Contact',
  [REGISTRY_BLOCK.type.legalImprint]: 'Imprint',
  [REGISTRY_BLOCK.type.legalPrivacy]: 'Privacy',
};

function humanizeToken(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function presentationOfFixture(fixture: BlockFixtureDefinition): RegistryBlockPresentation {
  const fromBlock = fixture.block.registryBlockKey;
  if (fromBlock) {
    return fromBlock as RegistryBlockPresentation;
  }
  return COMPONENT_TYPE_REGISTRY_KEYS[fixture.componentType] as RegistryBlockPresentation;
}

export function blockTypeForComponentType(
  componentType: LayoutComponentType,
): RegistryBlockType | undefined {
  const presentation = COMPONENT_TYPE_REGISTRY_KEYS[componentType];
  return resolveBlockTypeForPresentation(presentation);
}

export function blockTypeForFixture(fixture: BlockFixtureDefinition): RegistryBlockType | undefined {
  return resolveBlockTypeForPresentation(presentationOfFixture(fixture));
}

export function listLabBlockOptions(): LabBlockOption[] {
  const seen = new Set<RegistryBlockType>();
  const options: LabBlockOption[] = [];
  for (const componentType of IMPLEMENTED_COMPONENT_TYPES) {
    const type = blockTypeForComponentType(componentType);
    if (!type || seen.has(type)) continue;
    // Design Lab preview needs a fixture. Skip implemented types with no seed content.
    if (fixturesForBlockType(type).length === 0) continue;
    seen.add(type);
    options.push({
      type,
      label: BLOCK_LABELS[type] ?? humanizeToken(type),
    });
  }
  return options;
}

function fixturesForBlockType(blockType: RegistryBlockType): BlockFixtureDefinition[] {
  return BLOCK_FIXTURES.filter((fixture) => blockTypeForFixture(fixture) === blockType);
}

function arrangementLabel(
  presentation: RegistryBlockPresentation,
  blockType: RegistryBlockType,
  fixtureLabel?: string,
): string {
  /* Prefer curated fixture labels (e.g. Back to top / Transparent icon). */
  if (fixtureLabel) {
    const slash = fixtureLabel.indexOf(' / ');
    if (slash >= 0) return fixtureLabel.slice(slash + 3);
    return fixtureLabel;
  }
  const typePrefix = `${blockType}_`;
  if (presentation.startsWith(typePrefix)) {
    return humanizeToken(presentation.slice(typePrefix.length));
  }
  return humanizeToken(presentation);
}

/**
 * Arrangements for a block type: fixture variants first, then catalog presentations
 * that have no Design Lab fixture yet (previewed on the type’s default fixture).
 */
export function listLabArrangements(blockType: RegistryBlockType): LabArrangementOption[] {
  const fixtures = fixturesForBlockType(blockType);
  const base = fixtures[0];
  if (!base) return [];

  const options: LabArrangementOption[] = fixtures.map((fixture) => {
    const presentation = presentationOfFixture(fixture);
    const baseLabel = arrangementLabel(presentation, blockType, fixture.label);
    // Distinguish content stress fixtures that share a presentation (e.g. long headline).
    const siblings = fixtures.filter((entry) => presentationOfFixture(entry) === presentation);
    const label =
      siblings.length > 1 && fixture.label.toLowerCase().includes('long')
        ? `${baseLabel} · long headline`
        : siblings.length > 1 && fixture.id !== siblings[0]!.id
          ? arrangementLabel(presentation, blockType, fixture.label)
          : baseLabel;
    return {
      id: fixture.id,
      label,
      fixtureId: fixture.id,
      componentType: fixture.componentType,
      presentation,
    };
  });

  const covered = new Set(options.map((entry) => entry.presentation));
  for (const presentation of listPresentationsForBlockType(blockType)) {
    if (covered.has(presentation)) continue;
    const componentType =
      resolveComponentTypeForRegistryBlock(presentation) ?? base.componentType;
    if (!(IMPLEMENTED_COMPONENT_TYPES as readonly string[]).includes(componentType)) {
      continue;
    }
    options.push({
      id: `pres:${presentation}`,
      label: arrangementLabel(presentation, blockType),
      fixtureId: base.id,
      componentType,
      presentation,
    });
  }

  return options;
}

export function listLabLooks(blockType: RegistryBlockType): LabLookOption[] | null {
  if (blockType === REGISTRY_BLOCK.type.navigation) {
    return NAV_PRESENTATION_VALUES.map((value) => ({
      value,
      label: LOOK_LABELS[value],
    }));
  }
  if (blockType === REGISTRY_BLOCK.type.imageGallery) {
    return GALLERY_LIGHTBOX_VALUES.map((value) => ({
      value,
      label: GALLERY_LOOK_LABELS[value],
    }));
  }
  return null;
}

export function parseLabLook(
  blockType: RegistryBlockType,
  lookParam: string | null | undefined,
  presentation?: string | null,
): string | null {
  if (blockType === REGISTRY_BLOCK.type.navigation) {
    return lookParam && isNavPresentation(lookParam) ? lookParam : 'classic';
  }
  if (blockType === REGISTRY_BLOCK.type.imageGallery) {
    return lookParam && isGalleryLightbox(lookParam)
      ? lookParam
      : harmonizedGalleryLightbox(presentation);
  }
  return null;
}

export function resolveLabArrangement(
  blockType: RegistryBlockType,
  arrangementId: string | undefined,
  fixtureId: string | undefined,
): LabArrangementOption | undefined {
  const options = listLabArrangements(blockType);
  if (arrangementId) {
    const byId = options.find((entry) => entry.id === arrangementId);
    if (byId) return byId;
    const byPresentation = options.find((entry) => entry.presentation === arrangementId);
    if (byPresentation) return byPresentation;
  }
  if (fixtureId) {
    const byFixture = options.find((entry) => entry.fixtureId === fixtureId);
    if (byFixture) return byFixture;
  }
  return options[0];
}

export function defaultLookForBlockType(
  blockType: RegistryBlockType,
  presentation?: string | null,
): string | undefined {
  return parseLabLook(blockType, null, presentation) ?? undefined;
}
